/**
 * T230 — Core Web Vitals reporting.
 *
 * Captures LCP, CLS, INP, FCP, TTFB via the web-vitals package and posts
 * them to Application Insights' /v2/track endpoint as customMetrics.
 *
 * In dev (no `appInsightsConnectionString` in runtime config), reports
 * to console only so the values are visible during local debugging.
 *
 * No PII leaves the browser — only the metric name, value, route path,
 * and a session-scoped random id. Per PRD §10 + Constitution VII.
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { getConfig } from '@/lib/config';

const SESSION_ID = crypto.randomUUID();

interface AIConnectionString {
  ingestionEndpoint: string;
  instrumentationKey: string;
}

function parseConnectionString(cs: string): AIConnectionString | null {
  const parts = Object.fromEntries(
    cs.split(';').map((p) => {
      const idx = p.indexOf('=');
      return idx === -1 ? [p, ''] : [p.slice(0, idx), p.slice(idx + 1)];
    }),
  );
  const key = parts.InstrumentationKey;
  const ingestion = parts.IngestionEndpoint ?? 'https://dc.services.visualstudio.com/';
  return key ? { ingestionEndpoint: ingestion.replace(/\/$/, ''), instrumentationKey: key } : null;
}

function reportToAppInsights(metric: Metric, ai: AIConnectionString) {
  // Application Insights v2 Track-API envelope. sendBeacon (fire-and-forget,
  // survives navigation) when available; falls back to fetch with keepalive.
  const body = JSON.stringify({
    name: `Microsoft.ApplicationInsights.${ai.instrumentationKey}.Metric`,
    time: new Date().toISOString(),
    iKey: ai.instrumentationKey,
    tags: { 'ai.session.id': SESSION_ID },
    data: {
      baseType: 'MetricData',
      baseData: {
        ver: 2,
        metrics: [{ name: metric.name, value: metric.value, count: 1 }],
        properties: {
          rating: metric.rating,
          route: window.location.pathname,
          navigationType: metric.navigationType,
        },
      },
    },
  });
  const url = `${ai.ingestionEndpoint}/v2/track`;
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
  } else {
    void fetch(url, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } });
  }
}

let started = false;

export function startWebVitals() {
  if (started) return;
  started = true;
  const cs = (getConfig() as { appInsightsConnectionString?: string }).appInsightsConnectionString;
  const ai = cs ? parseConnectionString(cs) : null;

  const handler = (metric: Metric) => {
    if (ai) {
      try { reportToAppInsights(metric, ai); }
      catch (e) { console.warn('[webVitals] AI report failed:', e); }
    } else if (import.meta.env.DEV) {
      console.debug(`[webVitals] ${metric.name} = ${metric.value.toFixed(2)} (${metric.rating})`);
    }
  };
  onCLS(handler);
  onFCP(handler);
  onINP(handler);
  onLCP(handler);
  onTTFB(handler);
}
